import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Lock, Globe, User, Key } from 'lucide-react';
import { submitCollectorPortalRequest } from '@/services/api/collector';
import { useToast } from '@/hooks/use-toast';

interface PortalRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PortalRequestModal({ open, onOpenChange }: PortalRequestModalProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    portalName: '',
    portalUrl: '',
    username: '',
    password: '',
    description: '',
    contactEmail: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Basic guard
    if (!formData.portalName || !formData.portalUrl || !formData.username || !formData.password || !formData.contactEmail) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Pull authUserId from sessionStorage if available
      let authUserId: string | number | null = null;
      try {
        const raw = sessionStorage.getItem('userData');
        if (raw) {
          const parsed = JSON.parse(raw);
          authUserId = parsed?.authUserId || parsed?.authuserId || parsed?.AuthUserId || parsed?.id || null;
        }
      } catch {}

      const payload = {
        supplierName: formData.portalName,
        url: formData.portalUrl,
        username: formData.username,
        password: formData.password,
        email: formData.contactEmail,
        additionalInfo: formData.description || undefined,
        authUserId: authUserId ?? undefined,
      };

      await submitCollectorPortalRequest(payload);

      setIsSubmitted(true);
      toast({
        title: 'Request submitted',
          description: 'We will review your portal request and get back to you soon.',
      });

      // Reset form after a short delay
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          portalName: '',
          portalUrl: '',
          username: '',
          password: '',
          description: '',
          contactEmail: '',
        });
        onOpenChange(false);
      }, 2000);
    } catch (err: any) {
      console.error('[PortalRequest] submit error', err);
      toast({
        title: 'Submission failed',
        description: err?.response?.data?.message || err?.message || 'Unable to submit request. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isSubmitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-800 mb-2">Request Submitted Successfully!</h3>
            <p className="text-sm text-muted-foreground">
              Your portal integration request has been received. Our team will review it and get back to you within 2-3 business days.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            Portal Integration Request
          </DialogTitle>
          <DialogDescription>
            Request integration for a new portal. All information will be securely handled and encrypted.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="portalName">Portal Name *</Label>
              <Input
                id="portalName"
                value={formData.portalName}
                onChange={(e) => handleInputChange('portalName', e.target.value)}
                placeholder="e.g., PG&E, ConEd, Duke Energy"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="portalUrl">Portal URL *</Label>
              <Input
                id="portalUrl"
                type="url"
                value={formData.portalUrl}
                onChange={(e) => handleInputChange('portalUrl', e.target.value)}
                placeholder="https://portal.example.com"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="username">Test Username *</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="Username for testing"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Test Password *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Password for testing"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contactEmail">Contact Email *</Label>
            <Input
              id="contactEmail"
              type="email"
              value={formData.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e.target.value)}
              placeholder="your.email@company.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Additional Details</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Any specific requirements, special login procedures, or additional information..."
              rows={3}
            />
          </div>

          <Alert>
            <Lock className="h-4 w-4" />
            <AlertDescription>
              <strong>Security Notice:</strong> All credentials are encrypted and stored securely. 
              Test credentials will be used only for integration testing and will be deleted after successful integration.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Submitting Request...
                </>
              ) : (
                <>
                  <User className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
